import { useId, type FormEvent } from "react";
import type { MarkSentInput, SentVia } from "@/api/invoices";
import { RequestError } from "@/components/RequestError";
import { SelectField, TextField } from "@/components/ui/field";
import { FormActions } from "@/components/ui/form-actions";
import { todayIso } from "@/lib/dates";
import { useFormValues, useSubmitState } from "@/lib/use-form";

/** Choices when recording by hand; e-mails sent from the app record themselves. */
const SENT_VIA_CHOICES: Record<SentVia, string> = {
  whatsapp: "WhatsApp",
  email: "E-mail sent outside the app",
  courier: "Courier",
  hand: "By hand",
  portal: "Client portal",
  other: "Other",
};

/** For sentences: "Sent to the client 15 Sep 2026 by e-mail". */
export const SENT_VIA_PHRASES: Record<SentVia, string> = {
  whatsapp: "on WhatsApp",
  email: "by e-mail",
  courier: "by courier",
  hand: "by hand",
  portal: "through the client portal",
  other: "",
};

type MarkSentFormProps = {
  onSubmit: (input: MarkSentInput) => Promise<unknown>;
  onCancel: () => void;
  onReload?: () => void;
};

/** Records that an invoice reached the client outside the app. */
export function MarkSentForm({ onSubmit, onCancel, onReload }: MarkSentFormProps) {
  const id = useId();
  const { values, set } = useFormValues<MarkSentInput>({
    sent_on: todayIso(),
    via: "whatsapp",
    note: "",
  });
  const { error, errors, saving, run } = useSubmitState();

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    void run(() => onSubmit({ ...values, note: values.note.trim() }));
  }

  return (
    <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-4">
      <RequestError error={error} onReload={onReload} />
      <div className="grid gap-4 md:grid-cols-2">
        <TextField
          id={`${id}-date`}
          label="Sent on"
          type="date"
          value={values.sent_on}
          onChange={(value) => set("sent_on", value)}
          error={errors.sent_on}
        />
        <SelectField
          id={`${id}-via`}
          label="Sent by"
          value={values.via}
          onChange={(value) => set("via", value as SentVia)}
          options={Object.entries(SENT_VIA_CHOICES).map(([value, label]) => ({ value, label }))}
          error={errors.via}
        />
        <TextField
          id={`${id}-note`}
          label="Note"
          value={values.note}
          onChange={(value) => set("note", value)}
          error={errors.note}
          hint="Optional, e.g. who received it"
          className="md:col-span-2"
        />
      </div>
      <FormActions saving={saving} submitLabel="Mark as sent" onCancel={onCancel} />
    </form>
  );
}
